import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, MapPin, Search, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const CITIES = [
    "Fort Lauderdale", "Boca Raton", "Delray Beach", "West Palm Beach", "Coral Springs",
    "Parkland", "Plantation", "Miramar", "Boynton Beach", "Pompano Beach", "Hollywood",
    "Davie", "Weston", "Sunrise", "Pembroke Pines", "Palm Beach Gardens", "Jupiter",
    "Wellington", "Lake Worth", "Deerfield Beach", "Coconut Creek", "Lighthouse Point",
    "Hillsboro Beach", "Lauderhill", "Tamarac", "Oakland Park", "Royal Palm Beach", "Palm Beach",
];

const checkCoverage = (query) => {
    const s = query.trim().toLowerCase();
    if (!s) return null;
    if (/^\d{5}$/.test(s)) {
        const prefix = parseInt(s.slice(0, 3), 10);
        return prefix >= 330 && prefix <= 334;
    }
    return CITIES.some((c) => c.toLowerCase().includes(s) || s.includes(c.toLowerCase()));
};

export const ServiceArea = () => {
    const [query, setQuery] = useState("");
    const [result, setResult] = useState(null); // { covered, label }

    const onCheck = (e) => {
        e.preventDefault();
        const covered = checkCoverage(query);
        if (covered === null) return;
        setResult({ covered, label: query.trim() });
    };

    return (
        <section id="area" className="scroll-mt-20 bg-bone py-20 md:py-32">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                <Reveal>
                    <Eyebrow>Service area</Eyebrow>
                    <h2 className="mt-5 font-serif text-2xl font-normal leading-[1.15] tracking-tight text-espresso sm:text-3xl md:text-4xl">
                        From the Broward line to{" "}
                        <span className="italic text-oak">the Palm Beaches.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-sm leading-relaxed text-clay md:text-base">
                        Our crews work the corridor between Broward and Palm Beach
                        County every day — coastal, inland and everywhere in between.
                        If you are close to the line, ask anyway.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-2">
                        {CITIES.slice(0, 12).map((c) => (
                            <span
                                key={c}
                                className="flex items-center gap-1.5 border border-line bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-clay"
                            >
                                <MapPin className="h-3 w-3 text-oak" />
                                {c}
                            </span>
                        ))}
                        <span className="flex items-center px-2 font-mono text-[10px] uppercase tracking-[0.14em] text-fog">
                            + 16 more
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="relative overflow-hidden bg-espresso p-8 text-bone shadow-frame md:p-12">
                        <div
                            className="texture-draft absolute inset-0 opacity-20 invert"
                            aria-hidden="true"
                        />
                        <div className="relative">
                            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-wood">
                                Coverage checker
                            </p>
                            <h3 className="mt-4 font-serif text-2xl font-medium md:text-3xl">
                                Do we work in your neighborhood?
                            </h3>
                            <form onSubmit={onCheck} className="mt-8 flex flex-col gap-3 sm:flex-row">
                                <input
                                    data-testid="service-area-zip-input"
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="City or 5-digit ZIP code"
                                    aria-label="City or ZIP code"
                                    className="w-full border border-bone/20 bg-bone/10 px-5 py-4 text-sm text-bone placeholder:text-bone/40 focus:border-wood focus:outline-none focus:ring-2 focus:ring-wood/30"
                                />
                                <motion.button
                                    data-testid="service-area-check-button"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    type="submit"
                                    className="flex items-center justify-center gap-2 bg-oak px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-bone transition-colors hover:bg-oakdark"
                                >
                                    <Search className="h-4 w-4" />
                                    Check
                                </motion.button>
                            </form>

                            <div className="mt-6 min-h-[76px]">
                                <AnimatePresence mode="wait">
                                    {result === null ? (
                                        <motion.p
                                            key="idle"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="font-mono text-[11px] uppercase tracking-[0.16em] text-bone/50"
                                        >
                                            Try a city like Parkland — or a ZIP like 33486
                                        </motion.p>
                                    ) : result.covered ? (
                                        <motion.div
                                            key="covered"
                                            data-testid="service-area-result"
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -12 }}
                                            className="flex items-center gap-4 border border-wood/40 bg-wood/10 p-4"
                                        >
                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-wood text-espresso">
                                                <Check className="h-5 w-5" />
                                            </span>
                                            <p className="text-sm text-bone">
                                                <span className="font-semibold">Yes — we serve {result.label}.</span>{" "}
                                                Scroll down and tell us about your project.
                                            </p>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="not-covered"
                                            data-testid="service-area-result"
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -12 }}
                                            className="flex items-center gap-4 border border-bone/20 bg-bone/5 p-4"
                                        >
                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bone/15 text-bone">
                                                <X className="h-5 w-5" />
                                            </span>
                                            <p className="text-sm text-bone/80">
                                                <span className="font-semibold text-bone">
                                                    {result.label} is outside our current route.
                                                </span>{" "}
                                                We focus on Broward &amp; Palm Beach Counties.
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
